
import { PlayerState, setPlayerBoardState } from '../gameState';
import { Card, allCards } from '../data/cards';
import { TAVERN_UPGRADE_COSTS } from '../data/gameConfig';
import { CardInstance } from '../synergyHelpers';
import { countTribesOnBoard } from '../tribeSystem';
import { Hero } from '../data/heroes';

// AI difficulty settings
export type AIDifficulty = 'easy' | 'medium' | 'hard';

interface DifficultyConfig {
  rerollChance: number;      // Chance to reroll for better cards
  upgradeThreshold: number;  // How far behind desired tier before upgrading
  positioningEnabled: boolean; // Whether to use strategic positioning
  heroPowerUsage: number;    // Chance to use hero power when available (0-1)
}

/**
 * A strategic curve for when the AI should prioritize leveling up.
 * This ensures the AI stays on a competitive tier progression.
 * @param round The current round number.
 * @returns The tavern tier the AI should be aiming for.
 */
function getDesiredTierForRound(round: number): number {
  if (round < 3) return 1;  // Stay T1 for turn 1, 2
  if (round < 5) return 2;  // Aim for T2 on turn 3 (5g)
  if (round < 7) return 3;  // Aim for T3 on turn 5 (7g)
  if (round < 9) return 4;  // Aim for T4 on turn 7 (9g)
  if (round < 11) return 5; // Aim for T5 on turn 9 (10g)
  return 6;                 // Aim for T6 on turn 11 (10g)
}

// Helper to get cost to upgrade to the next tier.
function getUpgradeCost(currentTier: number): number {
    const nextTier = currentTier + 1;
    if (nextTier >= TAVERN_UPGRADE_COSTS.length) {
        return Infinity; // Cannot upgrade further
    }
    return TAVERN_UPGRADE_COSTS[nextTier];
}

// Helper to simulate a shop roll for the AI.
function generateAIShop(tier: number): Card[] {
    const available = allCards.filter(c => c.tier <= tier && !c.tags.includes('starter') && c.tier > 0);
    const shuffled = [...available].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 5); // 5 cards in the shop
}

/**
 * Scores a card based on its stats, faction synergy, and tags.
 * @param card The card to score.
 * @param board The current board state of the player.
 * @param roundNumber The current game round.
 * @returns A numerical score for the card.
 */
function scoreCard(card: Card, board: (Card | null)[], roundNumber: number): number {
    let score = card.attack + card.health;

    // --- Part 1: Tribe Synergy Scoring ---
    const boardCards = board.filter(Boolean) as CardInstance[];
    const tribeCounts = countTribesOnBoard(boardCards);

    if (card.tribe) {
        const currentCount = tribeCounts.get(card.tribe) || 0;
        const newCount = currentCount + 1;

        // Bonus for reaching a breakpoint (2, 4, 6)
        if (newCount === 2 || newCount === 4 || newCount === 6) {
            score += 5; // Big bonus for hitting a breakpoint
        } else if (currentCount >= 2) {
            score += 3; // Already have synergy active, more is good
        } else {
            score += 1; // Starting a new tribe
        }
    }

    // --- Part 2: Keyword/Tag Evaluation ---
    if (card.tags.includes('scaling')) {
        // Scaling is more valuable in the early to mid-game.
        score += Math.max(2, 8 - roundNumber);
    }
    if (card.tags.includes('economy')) {
        // Economy is always useful.
        score += 4;
    }
    if (card.tags.includes('lastbreath')) {
        const lastbreathCountOnBoard = board.filter(c => c && c.tags.includes('lastbreath')).length;
        score += 2 + lastbreathCountOnBoard; // Base value + synergy
    }
    if (card.tags.includes('summon')) {
        score += 2; // Immediate value
    }
    if (card.tags.includes('guard')) {
        // Guard is good, especially with high health
        score += card.health * 0.5;
    }

    return score;
}

// Difficulty configurations
const DIFFICULTY_CONFIGS: Record<AIDifficulty, DifficultyConfig> = {
  easy: {
    rerollChance: 0.2,
    upgradeThreshold: 2,
    positioningEnabled: false,
    heroPowerUsage: 0.3
  },
  medium: {
    rerollChance: 0.5,
    upgradeThreshold: 1,
    positioningEnabled: true,
    heroPowerUsage: 0.6
  },
  hard: {
    rerollChance: 0.8,
    upgradeThreshold: 0,
    positioningEnabled: true,
    heroPowerUsage: 0.9
  }
};

/**
 * Strategically positions units on the board.
 * Guards go in front (left), high attack in middle, support/scaling in back (right).
 */
function positionBoard(board: (Card | null)[]): (Card | null)[] {
  const units = board.filter(Boolean) as Card[];
  if (units.length === 0) return board;

  // Sort by priority: guards first, then by attack
  units.sort((a, b) => {
    const aGuard = a.tags.includes('guard') ? 1 : 0;
    const bGuard = b.tags.includes('guard') ? 1 : 0;

    // Guards go first
    if (aGuard !== bGuard) return bGuard - aGuard;

    // Support/scaling units go last (lower attack priority)
    const aSupport = a.tags.includes('scaling') || a.tags.includes('economy') ? 1 : 0;
    const bSupport = b.tags.includes('scaling') || b.tags.includes('economy') ? 1 : 0;
    if (aSupport !== bSupport) return aSupport - bSupport;

    // Otherwise sort by attack (higher attack in front)
    return b.attack - a.attack;
  });

  // Create new board with positioned units
  const newBoard: (Card | null)[] = new Array(7).fill(null);
  units.forEach((unit, i) => {
    if (i < 7) newBoard[i] = unit;
  });

  return newBoard;
}

/**
 * Attempts to use the AI's hero power if appropriate.
 */
function tryUseHeroPower(player: PlayerState, config: DifficultyConfig): void {
  if (!player.hero || player.heroPowerUsedThisTurn) return;
  if (Math.random() > config.heroPowerUsage) return;

  const hero = player.hero;
  const cost = hero.cost || 0;

  // Check if we can afford it
  if (player.gold < cost) return;

  // Use power based on type
  switch (hero.powerType) {
    case 'passive_on_buy':
    case 'passive_on_sell':
    case 'passive_on_refresh':
    case 'passive_frontline_health':
    case 'passive_first_strike':
    case 'passive_stat_swap':
      // Passive powers don't need activation
      break;

    case 'active_buff_shop':
    case 'active_buff_board':
      // Active powers that buff - use if we have targets
      if (hero.powerType === 'active_buff_board' && player.board.some(c => c !== null)) {
        player.gold -= cost;
        player.heroPowerUsedThisTurn = true;
        // Apply buff to random board unit
        const units = player.board.filter(Boolean) as Card[];
        if (units.length > 0 && hero.powerConfig) {
          const target = units[Math.floor(Math.random() * units.length)];
          target.attack += hero.powerConfig.attack || 0;
          target.health += hero.powerConfig.health || 0;
        }
      }
      break;

    case 'passive_gold_on_sell':
    case 'passive_discount':
    case 'end_of_turn_gold':
      // Economy powers - passive or automatic
      break;

    default:
      break;
  }
}

/**
 * Runs a simple tavern phase AI for a single bot player.
 * @param player The bot's PlayerState object to be modified.
 * @param roundNumber The current round number of the game.
 * @param difficulty The AI difficulty level.
 */
export function runTavernAI(player: PlayerState, roundNumber: number, difficulty: AIDifficulty = 'medium'): void {
  if (player.isHuman || !player.alive) {
    return;
  }

  const config = DIFFICULTY_CONFIGS[difficulty];

  // 0. Try to use hero power at start of turn
  tryUseHeroPower(player, config);

  // 1. Leveling Phase
  const desiredTier = getDesiredTierForRound(roundNumber);
  const upgradeCost = getUpgradeCost(player.tavernTier);
  const tierDifference = desiredTier - player.tavernTier;

  // Upgrade if behind by more than threshold
  if (tierDifference > config.upgradeThreshold && player.gold >= upgradeCost && player.tavernTier < TAVERN_UPGRADE_COSTS.length - 1) {
    player.gold -= upgradeCost;
    player.tavernTier++;
  } else if (player.tavernTier < desiredTier && player.gold >= upgradeCost && player.tavernTier < TAVERN_UPGRADE_COSTS.length - 1) {
    player.gold -= upgradeCost;
    player.tavernTier++;
  }

  // 2. Buy/Replace Phase
  let rerolls = 1; // Give the AI a chance to reroll if the shop is bad
  while (player.gold >= 3) {
    const shop = generateAIShop(player.tavernTier);
    if (shop.length === 0) break; // No cards to buy

    const scoredShop = shop.map(card => ({ card, score: scoreCard(card, player.board, roundNumber) }));
    scoredShop.sort((a, b) => b.score - a.score);
    const bestShopCard = scoredShop[0];

    const boardUnits = player.board.filter(c => c !== null) as Card[];
    
    if (boardUnits.length < 7) {
        // --- Fill the board first ---
        player.gold -= 3;
        const newCardInstance: Card = { ...bestShopCard.card, instanceId: crypto.randomUUID() };
        const emptySlotIndex = player.board.findIndex(slot => slot === null);
        player.board[emptySlotIndex] = newCardInstance;
    } else {
        // --- Board is full, consider replacing a weaker unit ---
        if (player.gold < 3) break; // Can't afford to buy, so can't replace

        const scoredBoard = boardUnits.map(card => ({ card, score: scoreCard(card, player.board, roundNumber) }));
        scoredBoard.sort((a, b) => a.score - b.score);
        const weakestBoardUnit = scoredBoard[0];
        
        // Threshold of +2 to score to justify the net cost of 2 gold (sell for 1, buy for 3)
        if (bestShopCard.score > weakestBoardUnit.score + 2) {
            const weakestUnitIndex = player.board.findIndex(c => c && c.instanceId === weakestBoardUnit.card.instanceId);
            if (weakestUnitIndex !== -1) {
                player.gold -= 2; // Net cost of selling and buying
                const newCardInstance: Card = { ...bestShopCard.card, instanceId: crypto.randomUUID() };
                player.board[weakestUnitIndex] = newCardInstance;
            }
        } else if (rerolls > 0 && player.gold >= 1) {
            // Reroll if no good upgrade is available and we can afford it
            rerolls--;
            player.gold -= 1;
            continue; // Go to next loop iteration with a new shop
        } else {
            // No good buys and no rerolls, stop the phase.
            break;
        }
    }
  }

  // 3. Strategic Positioning
  if (config.positioningEnabled) {
    player.board = positionBoard(player.board);
  }

  // 4. Try hero power again at end of turn if we have gold left
  if (player.gold >= (player.hero?.cost || 0)) {
    tryUseHeroPower(player, config);
  }

  // Mark AI as ready after completing their turn
  setPlayerBoardState(player.id, 'ready');
}

/**
 * Selects a hero for an AI player based on difficulty.
 */
export function selectHeroForAI(availableHeroes: Hero[], difficulty: AIDifficulty = 'medium'): Hero {
  if (availableHeroes.length === 0) {
    throw new Error('No heroes available for selection');
  }

  const config = DIFFICULTY_CONFIGS[difficulty];

  // Easy AI picks randomly
  if (difficulty === 'easy') {
    return availableHeroes[Math.floor(Math.random() * availableHeroes.length)];
  }

  // Medium/Hard AI prefers certain power types
  const preferredTypes = [
    'passive_on_buy',
    'passive_on_refresh',
    'passive_gold_on_sell',
    'passive_discount',
    'active_buff_board'
  ];

  // Score heroes
  const scoredHeroes = availableHeroes.map(hero => {
    let score = Math.random() * 2; // Base randomness

    // Prefer economy and buff powers
    if (preferredTypes.includes(hero.powerType)) {
      score += 3;
    }

    // Hard AI values active powers more
    if (difficulty === 'hard' && hero.isActive) {
      score += 2;
    }

    return { hero, score };
  });

  // Sort by score and pick best
  scoredHeroes.sort((a, b) => b.score - a.score);
  return scoredHeroes[0].hero;
}
